import { useRef, useState } from "react";

import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  ProjectSharingBaseRoles,
  useDeleteProjectUser,
  useUpdateProjectUser,
} from "../../../../../hooks/useProjectSharing";
import type { ProjectViewLinkMember } from "../../../../../hooks/useProjectViewLink";
import useTracker from "../../../../../utils/useTracker";
import { MemberRow } from "./MemberRow";

interface MemberRowProps {
  member: ProjectViewLinkMember;
}

const projectInvitationSchema = z.object({
  baseRole: z.union([
    z.literal(ProjectSharingBaseRoles.Viewer),
    z.literal(ProjectSharingBaseRoles.Commenter),
  ]),
});

export const MemberRowProject = ({ member }: MemberRowProps) => {
  const tracker = useTracker();
  const { projectId = "" } = useParams();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const animationPromiseResolver = useRef<(() => void) | null>(null);

  const {
    mutateAsync: mutateUpdateProjectUser,
    isPending: isUpdatePending,
    isError: isUpdateError,
  } = useUpdateProjectUser({});
  const {
    mutateAsync: mutateDeleteProjectUser,
    isPending: isDeletePending,
    isError: isDeleteError,
    removeUserFromCache,
  } = useDeleteProjectUser({
    onSuccess: async () => {
      setDeleteConfirmationOpen(false);
      const animationPromise = new Promise<void>((resolve) => {
        animationPromiseResolver.current = resolve;
      });
      await animationPromise;
      removeUserFromCache(member.userId);
    },
    onError: () => {
      setDeleteConfirmationOpen(true);
    },
  });
  const isPending = isDeletePending || isUpdatePending;

  return (
    <MemberRow
      schema={projectInvitationSchema}
      member={member}
      deleteConfirmationOpen={deleteConfirmationOpen}
      onUpdate={(prevBaseRole, newBaseRole) => {
        tracker.trackShareModalMemberRoleChanged({
          projectId: projectId,
          userId: member.userId,
          oldValue: prevBaseRole,
          newValue: newBaseRole,
          sharedResource: "project",
        });
        return mutateUpdateProjectUser({
          userId: member.userId,
          baseRole: newBaseRole,
        });
      }}
      onDelete={() => {
        tracker.trackShareModalMemberRemoveConfirmed({
          projectId: projectId,
          userId: member.userId,
          sharedResource: "project",
        });
        return mutateDeleteProjectUser(member.userId);
      }}
      onOpenDeleteConfirmation={() => setDeleteConfirmationOpen(true)}
      onCloseDeleteConfirmation={() => setDeleteConfirmationOpen(false)}
      animationPromiseResolver={animationPromiseResolver}
      isPending={isPending}
      isError={isUpdateError || isDeleteError}
    />
  );
};
